import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { Address, IPopupModelV2, ModalChild, Product, Sparte } from '@mwe/models';
import { ClickableProductsCardComponent } from '../cards/clickable-products-card/clickable-products-card.component';
import { ModalComponent } from '../modal/modal.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { containsSomeCategoryInProducts, getCompanyCorrelationFromCategory, translateCategory } from '@mwe/utils';
import { AddLinkImagePipe, SafeHtmlPipe } from '@mwe/ui/pipes';
import { EmobCancellationStore, MoveOutLogic } from '@mwe/services';

@Component({
  selector: 'mwe-cancellation-modal',
  templateUrl: './cancellation-modal.html',
  imports: [ModalComponent, TranslatePipe, ClickableProductsCardComponent, AddLinkImagePipe, SafeHtmlPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CancellationModalComponent implements ModalChild {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly emobilityCancelationStore = inject(EmobCancellationStore);
  private readonly moveOutLogic = inject(MoveOutLogic);
  private readonly translateService = inject(TranslateService);

  readonly popupModel = signal<IPopupModelV2>(null);
  protected readonly internetTelevisionProductsString = signal('');

  private readonly address = computed<Address>(() => this.popupModel()?.componentData?.address);
  private readonly products = computed<Product[]>(() => this.popupModel()?.componentData?.products);
  protected readonly emobilityCategories = computed<Sparte[]>(() => containsSomeCategoryInProducts(this.products(), [Sparte.Emobility]));
  protected readonly internetCategories = computed<Sparte[]>(() => containsSomeCategoryInProducts(this.products(), [Sparte.Internet]));
  protected readonly televisionCategories = computed<Sparte[]>(() => containsSomeCategoryInProducts(this.products(), [Sparte.Iptv]));
  protected readonly stromCategories = computed<Sparte[]>(
    () =>
      containsSomeCategoryInProducts(this.products(), [Sparte.Strom, Sparte.Nachtstrom, Sparte.Stromrueck]).filter(
        category => category !== Sparte.Nachtstrom && category !== Sparte.Stromrueck,
      ), // filter this value as Nachstrom and Stromrueck should not be displayed)
  );
  protected readonly gasCategories = computed<Sparte[]>(() => containsSomeCategoryInProducts(this.products(), [Sparte.Gas]));
  protected readonly waermeCategories = computed<Sparte[]>(() => containsSomeCategoryInProducts(this.products(), [Sparte.Fernwaerme]));

  protected readonly internetTelevisionCategories = computed<Sparte[]>(() => [
    ...this.internetCategories(),
    ...this.televisionCategories(),
  ]);
  protected readonly stromGasWaermeCategories = computed<Sparte[]>(() => [
    ...this.stromCategories(),
    ...this.gasCategories(),
    ...this.waermeCategories(),
  ]);

  protected readonly hasEmobility = computed<boolean>(() => this.emobilityCategories().length > 0);
  protected readonly hasInternetTelevision = computed<boolean>(() => this.internetTelevisionCategories().length > 0);
  protected readonly hasStromGas = computed<boolean>(() => [...this.stromCategories(), ...this.gasCategories()].length > 0);
  protected readonly hasWaerme = computed<boolean>(() => this.waermeCategories().length > 0);
  protected readonly hasStromGasWaerme = computed<boolean>(() => this.stromGasWaermeCategories().length > 0);

  protected readonly Sparte = Sparte;
  protected readonly stromCompany = getCompanyCorrelationFromCategory(Sparte.Strom);
  protected readonly waermeCompany = getCompanyCorrelationFromCategory(Sparte.Fernwaerme);
  protected readonly emobilityCompany = getCompanyCorrelationFromCategory(Sparte.Emobility);
  protected readonly internetCompany = getCompanyCorrelationFromCategory(Sparte.Internet);
  protected readonly link =
    'https://www.wienenergie.at/privat/hilfe-und-kontakt/kontakt-servicezentren/kontaktthemen/?thema=Internet%20%26%20TV_An-%20Ab-%20Ummeldung_Abmeldung&formular=1';

  constructor() {
    effect(async () => {
      const translations = await Promise.all(
        this.internetTelevisionCategories().map(category => translateCategory(category, this.translateService)),
      );
      this.internetTelevisionProductsString.set(translations.join(' & '));
    });
  }

  startEmobCancellation() {
    this.closePopup();
    this.emobilityCancelationStore.initEmobCancellationProcess({ products: this.products(), address: this.address() });
  }

  startDefaultCancellation() {
    this.closePopup();
    this.moveOutLogic.startProcessWithoutAddressSelection(this.products(), this.address());
  }

  private closePopup() {
    this.activeModal.close();
  }
}
